import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../components/user/BCPAuthProvider";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Grid,
  IconButton,
  Link,
  Tab,
  Tabs,
  Typography
} from "@mui/material";
import {
  findGetParameter,
  getData,
  deleteData,
  isUsingWS,
  newPath,
  postData
} from "../../components/utilities/UtilityFunctions";
import { postNotification } from "../../components/utilities/Notifications";
import ModalLoadingFull from "../../components/modals/ModalLoadingFull";

import moment from "moment";
import "moment-timezone";

import EventAbout from "./components/EventAbout";
import EventRoster from "./components/EventRoster";
import EventPlacings from "./components/EventPlacings";
import EventPairings from "./components/EventPairings";
import EventPlayerDetails from "./components/EventPlayerDetails";
import { getEvent } from "./components/EventFunctions";
import EventTeamDetails from "./components/EventTeamDetails";

import {
  BCPShareNodes,
  BCPStar,
  BCPTrash
} from "../../components/icons/bcp/BCPIcons";

import TabPanel, { tabPanelA11yProps } from "../../components/layout/TabPanel";
import ShareModal from "../../components/modals/ShareModal";
import { useConfirm } from "material-ui-confirm";
import EventBrackets from "./components/EventBrackets";
import EventPlayerSoftScores from "./components/EventPlayerSoftScores";
import { getTab } from "./utils";
import EventHelmet from "./components/SubComponents/EventHelmet";
import { isAppMode } from "../../global/AppOverlay";
import { getEnvironmentVariable } from "../../global/environmentVariables";
import EventPairingsWS from "./components/EventPairings/EventPairingsWS";
import GWMyWarhammerAlert from "../../components/alerts/DefaultAlerts";
import { setCookie } from "../../components/layout/cookies/CookieFunctions";
const NO_SUBSCRIPTIONS = getEnvironmentVariable("NO_SUBSCRIPTIONS") === "true";

var tabIndexes = {
  overview: 0,
  roster: 1,
  pairings: 2,
  placings: 3
};

const appMode = isAppMode();
const disableTabs = findGetParameter("disableTabs") === "true";

export default function EventHome() {
  let { eventId } = useParams();
  const authContext = useContext(AuthContext);
  const { t } = useTranslation("translation", {
    keyPrefix: "components.EventTabs"
  });
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [event, setEvent] = useState(null);

  const useWebSockets = isUsingWS(authContext);

  const activeTabParam = findGetParameter("active_tab");

  const [activeTab, setActiveTab] = useState(null);
  const [following, setFollowing] = useState(
    Boolean(event?.userFollowing || event?.following)
  );
  const [processing, setProcessing] = useState(false);

  const followEvent = async () => {
    if (!authContext?.authState?.identity?.userId) {
      return;
    }
    try {
      setProcessing(true);
      await postData(
        `/events/${event?.id}/${following ? "unfollow" : "follow"}`
      );
      setFollowing(!following);
    } catch (err) {
      postNotification("error", err);
    }
    setProcessing(false);
  };

  const handleTabChange = async (e, newValue) => {
    setLoading(true);
    for (let tabIndex of Object.keys(tabIndexes)) {
      if (tabIndexes[tabIndex] === newValue) {
        if (tabIndex === "organize") {
          if (appMode) setLoading(false);
          return true;
        }
      }
    }

    let freshEvent = event;

    // Handles switching between overview and other tabs that require different data and checks if the overview is already loaded to avoid unnecessary API calls.
    if (activeTab === 0 && newValue !== 0 && !freshEvent?.overview) {
      try {
        freshEvent = await handleRefreshEvent(newValue);
        setEvent(freshEvent);
      } catch (err) {
        return;
      }
    }

    try {
      await hasAccess(freshEvent, authContext, newValue, false);
    } catch (err) {
      try {
        const freshEvent = await getEvent(eventId, authContext, {
          player: true,
          photo: activeTab === 0,
          role: true,
          following: activeTab === 0,
          overview: true
        });

        if (freshEvent?.following || freshEvent?.userFollowing)
          setFollowing(true);

        await hasAccess(freshEvent, authContext, newValue, true);
        setEvent(freshEvent);
      } catch (err) {
        setLoading(false);
        return;
      }
    }

    let path;
    Object.keys(tabIndexes).forEach((tabIndex) => {
      if (tabIndexes[tabIndex] === newValue) {
        const params = {
          active_tab: tabIndex
        };
        if (newValue !== tabIndexes.pairings) params.round = null;
        else params.round = event?.currentRound || 1;
        path = newPath(params);

        return true;
      }
    });
    setActiveTab(newValue);
    navigate(path, { replace: appMode ? true : false });
    setLoading(false);
  };

  const handleRefreshEvent = async (newTab) => {
    var freshEvent = await getEvent(eventId, authContext, {
      player: true,
      photo: (newTab !== undefined ? newTab : activeTab) === 0,
      role: true,
      following: (newTab !== undefined ? newTab : activeTab) === 0,
      overview: true
    });

    if (freshEvent?.following || freshEvent?.userFollowing) setFollowing(true);

    var newTabName = "overview";
    for (let key of Object.keys(tabIndexes)) {
      if (tabIndexes[key] === (newTab !== undefined ? newTab : activeTab)) {
        newTabName = key;
        break;
      }
    }

    var tabIndex = 3;
    if (freshEvent?.doubleElimination) {
      tabIndex++;
      tabIndexes.brackets = tabIndex;
    }
    if (
      freshEvent?.player &&
      freshEvent?.softScores?.filter((el) => el.playerScored)?.length > 0
    ) {
      tabIndex++;
      tabIndexes["player-soft-scores"] = tabIndex;
    }
    if (freshEvent?.player) {
      tabIndex++;
      tabIndexes["player-details"] = tabIndex;
    }

    if (
      (freshEvent?.teamEvent ||
        freshEvent?.format?.teamEvent ||
        freshEvent?.doublesEvent ||
        freshEvent?.format?.doublesEvent) && // all doubles should be teams
      (freshEvent?.teamPlayer || freshEvent?.player)
    ) {
      tabIndex++;
      tabIndexes["team-details"] = tabIndex;
    }
    tabIndex++;
    tabIndexes["organize"] = tabIndex;

    setEvent(freshEvent);
    setActiveTab(tabIndexes[newTabName]);
    return freshEvent;
  };

  const getOverview = async () => {
    if (!eventId) return null;

    let returnObj;
    let playerObj;
    let teamPlayerObj;
    try {
      returnObj = await getData(`/events/${eventId}/overview`);

      try {
        playerObj = await getData(`/events/${eventId}/currentPlayer`);
      } catch {
        //  Will 404 if player is not registered to event
      }

      if (returnObj?.format?.teamEvent) {
        try {
          teamPlayerObj = await getData(`/events/${eventId}/currentTeamPlayer`);
        } catch {
          //  Will 404 if team is not registered to event
        }
      }

      if (playerObj?.id) {
        returnObj.player = playerObj;
      }

      if (teamPlayerObj?.id) {
        returnObj.teamPlayer = teamPlayerObj;
      }
    } catch (err) {
      console.error(err);
    }
    return returnObj;
  };

  useEffect(() => {
    const check = async () => {
      try {
        setLoading(true);
        var initialTab =
          tabIndexes[
            activeTabParam ? activeTabParam.toLowerCase() : "overview"
          ] !== undefined
            ? tabIndexes[
                activeTabParam ? activeTabParam.toLowerCase() : "overview"
              ]
            : 0;

        try {
          var freshEvent =
            initialTab === 0
              ? await getOverview()
              : await getEvent(eventId, authContext, {
                  player: true,
                  photo: true,
                  role: true,
                  following: true,
                  overview: true
                });

          if (freshEvent?.following || freshEvent?.userFollowing)
            setFollowing(true);
        } catch (err) {
          postNotification("error", t("notifications.errors.eventNotFound"));
          if (appMode) {
            navigate("/app/game");
          } else {
            navigate("/");
          }
        }

        if (freshEvent?.error) {
          throw new Error(freshEvent.error);
        }

        if (freshEvent?.id) {
          setCookie("lastEventId", freshEvent.id);
        }

        var tabIndex = 3;
        if (
          freshEvent?.doubleElimination ||
          freshEvent?.format?.bracketPairings
        ) {
          tabIndex++;
          tabIndexes.brackets = tabIndex;
        }
        if (
          freshEvent?.player &&
          freshEvent?.softScores?.filter((el) => el.playerScored)?.length > 0
        ) {
          tabIndex++;
          tabIndexes["player-soft-scores"] = tabIndex;
        }
        if (freshEvent?.player) {
          tabIndex++;
          tabIndexes["player-details"] = tabIndex;
        }
        if (
          (freshEvent?.teamEvent ||
            freshEvent?.format?.teamEvent ||
            freshEvent?.doublesEvent ||
            freshEvent?.format?.doublesEvent) && // all doubles should be teams
          (freshEvent?.teamPlayer || freshEvent?.player)
        ) {
          tabIndex++;
          tabIndexes["team-details"] = tabIndex;
        }
        tabIndex++;
        tabIndexes["organize"] = tabIndex;

        setEvent(freshEvent);
        initialTab = getTab(freshEvent, tabIndexes, activeTabParam);

        try {
          await hasAccess(freshEvent, authContext, initialTab, true);
        } catch (err) {
          initialTab = freshEvent.started ? tabIndexes.placings : 0;
        }
        setActiveTab(initialTab);
      } catch (err) {
        postNotification("error", err);
        console.error(err);
        setEvent(null);
      } finally {
        setLoading(false);
      }
    };
    check();
    // eslint-disable-next-line
  }, []);

  return (
    <>
      {event && <EventHelmet event={event} />}

      {Boolean(loading) && <ModalLoadingFull />}

      {event?.gameSystem?.manufacturer === "Games Workshop" &&
        !authContext?.authState?.identity?.hasMyWarhammer && (
          <GWMyWarhammerAlert authContext={authContext} />
        )}

      {event && !loading && (
        <Box sx={{ width: "100%" }}>
          {!disableTabs && (
            <EventTabs
              event={event}
              activeTab={activeTab}
              handleTabChange={handleTabChange}
              followEvent={followEvent}
              processing={processing}
              following={following}
            />
          )}

          {activeTab === tabIndexes.overview && (
            <TabPanel value={activeTab} index={tabIndexes.overview}>
              <EventAbout
                event={event}
                setEvent={setEvent}
                activeTab={activeTab}
                handleTabChange={handleTabChange}
                tabIndexes={tabIndexes}
                authContext={authContext}
                refreshEvent={handleRefreshEvent}
                followEvent={followEvent}
                processing={processing}
                following={following}
              />
            </TabPanel>
          )}
          {activeTab === tabIndexes.roster && (
            <TabPanel value={activeTab} index={tabIndexes.roster}>
              <EventRoster
                event={event}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                handleTabChange={handleTabChange}
                authContext={authContext}
              />
            </TabPanel>
          )}

          {activeTab === tabIndexes.pairings && (
            <TabPanel value={activeTab} index={tabIndexes.pairings}>
              {useWebSockets ? (
                <EventPairingsWS
                  event={event}
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  handleTabChange={handleTabChange}
                  authContext={authContext}
                />
              ) : (
                <EventPairings
                  event={event}
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  handleTabChange={handleTabChange}
                  authContext={authContext}
                />
              )}
            </TabPanel>
          )}

          {activeTab === tabIndexes.placings && (
            <TabPanel value={activeTab} index={tabIndexes.placings}>
              <EventPlacings
                event={event}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                handleTabChange={handleTabChange}
                authContext={authContext}
              />
            </TabPanel>
          )}

          {tabIndexes.brackets !== undefined &&
            activeTab === tabIndexes.brackets && (
              <TabPanel value={activeTab} index={tabIndexes.brackets}>
                <EventBrackets
                  event={event}
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  handleTabChange={handleTabChange}
                  authContext={authContext}
                />
              </TabPanel>
            )}

          {tabIndexes["player-soft-scores"] !== undefined &&
            activeTab === tabIndexes["player-soft-scores"] && (
              <TabPanel
                value={activeTab}
                index={tabIndexes["player-soft-scores"]}
              >
                <EventPlayerSoftScores
                  event={event}
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  handleTabChange={handleTabChange}
                  authContext={authContext}
                  setEvent={setEvent}
                />
              </TabPanel>
            )}

          {tabIndexes["player-details"] !== undefined &&
            activeTab === tabIndexes["player-details"] && (
              <TabPanel value={activeTab} index={tabIndexes["player-details"]}>
                <EventPlayerDetails
                  event={event}
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  handleTabChange={handleTabChange}
                  authContext={authContext}
                  setEvent={setEvent}
                />
              </TabPanel>
            )}
          {tabIndexes["team-details"] !== undefined &&
            activeTab === tabIndexes["team-details"] && (
              <TabPanel value={activeTab} index={tabIndexes["team-details"]}>
                <EventTeamDetails
                  event={event}
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  handleTabChange={handleTabChange}
                  authContext={authContext}
                  setEvent={setEvent}
                  handleRefreshEvent={handleRefreshEvent}
                />
              </TabPanel>
            )}
        </Box>
      )}
    </>
  );
}

export function EventTabs(props) {
  const {
    event,
    activeTab,
    handleTabChange,
    centered,
    followEvent,
    processing,
    following
  } = props;
  const authContext = useContext(AuthContext);
  const { t } = useTranslation("translation", {
    keyPrefix: "components.EventTabs"
  });
  const confirm = useConfirm();
  const navigate = useNavigate();

  const isOrganizer =
    authContext?.authState?.identity?.isAdmin === true ||
    event?.isTO ||
    event?.eventUsers?.[authContext?.authState?.identity?.userId] ||
    authContext?.authState?.identity?.adminGameSystems?.includes(
      event?.gameSystemId
    ) ||
    authContext?.authState?.identity?.adminGameSystems?.includes(
      event?.gameSystem?.id
    );

  const isAdminOrGameSystemAdmin =
    authContext?.authState?.identity?.isAdmin === true ||
    authContext?.authState?.identity?.adminGameSystems?.includes(
      event?.gameSystemId
    ) ||
    authContext?.authState?.identity?.adminGameSystems?.includes(
      event?.gameSystem?.id
    );

  const [rightCols, setRightCols] = useState(0);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const screenSize = window.innerWidth;

  const handleDeleteEvent = async () => {
    try {
      await confirm({
        title: t("deleteEvent") || "Delete Event",
        description:
          t("deleteEventConfirmation") ||
          "Are you sure you want to delete this event? This action cannot be undone.",
        dialogProps: {
          sx: {
            zIndex: 99999
          }
        }
      });
    } catch (err) {
      return;
    }

    try {
      setDeleteLoading(true);
      await deleteData(`/events/${event?.id}`);
      postNotification(
        "success",
        t("eventDeleted") || "Event deleted successfully"
      );
      navigate("/play/events");
    } catch (err) {
      postNotification("error", err);
    } finally {
      setDeleteLoading(false);
    }
  };

  useEffect(() => {
    var rc = isAdminOrGameSystemAdmin ? 3 : 2;
    setRightCols(rc);
  }, [props, isAdminOrGameSystemAdmin]);

  return (
    <Box
      sx={{
        maxWidth: "100%",
        backgroundColor: "banner.main",
        color: "banner.contrastText",
        borderBottom: 1,
        borderColor: "divider"
      }}
    >
      {(processing || deleteLoading) && <ModalLoadingFull />}
      <Container maxWidth="lg">
        <Grid container alignItems="center" pr={[2, 0]} pl={[2, 0]}>
          {!appMode && (
            <>
              <Grid
                item
                sx={{
                  flexGrow: 1,
                  maxWidth: `calc(100% - ${1 + rightCols}px)`
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    maxWidth: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontSize: screenSize > 900 ? "48px" : "24px",
                    pt: "auto",
                    pb: "auto"
                  }}
                >
                  {event?.name}
                </Typography>
              </Grid>

              {rightCols > 0 && (
                <Grid item sx={{ maxWidth: `${rightCols * 34}px` }}>
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={followEvent}
                  >
                    {<BCPStar outlined={!Boolean(following)} />}
                  </IconButton>
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShareModalOpen(true)}
                  >
                    <BCPShareNodes
                      style={{ "font-style": "20px", width: "20px" }}
                    />
                  </IconButton>
                  {isAdminOrGameSystemAdmin && (
                    <IconButton
                      size="small"
                      color="inherit"
                      onClick={handleDeleteEvent}
                      disabled={deleteLoading}
                      sx={{
                        color: "error.main"
                      }}
                    >
                      <BCPTrash
                        style={{ "font-style": "20px", width: "20px" }}
                      />
                    </IconButton>
                  )}
                </Grid>
              )}
            </>
          )}
        </Grid>

        <Box
          sx={{
            display: centered ? "flex" : "",
            justifyContent: centered ? "center" : "",
            width: "100%"
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="event tabs"
            textColor="inherit"
            centered={centered}
          >
            <Tab
              label={t("overview")}
              {...tabPanelA11yProps(tabIndexes.overview, "overview")}
            />
            <Tab
              label={t("roster")}
              {...tabPanelA11yProps(tabIndexes.roster, "roster")}
            />
            <Tab
              label={t("pairings")}
              disabled={event?.numberOfRounds === 0}
              {...tabPanelA11yProps(tabIndexes.pairings, "pairings")}
            />
            <Tab
              label={t("placings")}
              {...tabPanelA11yProps(tabIndexes.placings, "placings")}
            />

            {tabIndexes.brackets !== undefined && (
              <Tab
                label={t("brackets")}
                {...tabPanelA11yProps(tabIndexes.brackets, "brackets")}
              />
            )}

            {tabIndexes["player-soft-scores"] !== undefined && (
              <Tab
                label={t("softScores")}
                {...tabPanelA11yProps(
                  tabIndexes["player-soft-scores"],
                  "player-soft-scores"
                )}
              />
            )}

            {tabIndexes["player-details"] !== undefined && (
              <Tab
                label={t("playerDetails")}
                {...tabPanelA11yProps(
                  tabIndexes["player-details"],
                  "player-details"
                )}
              />
            )}
            {tabIndexes["team-details"] !== undefined && (
              <Tab
                label={t("teamDetails")}
                {...tabPanelA11yProps(
                  tabIndexes["team-details"],
                  "team-details"
                )}
              />
            )}
            {isOrganizer && (
              <Tab
                label={t("organize")}
                href={`/organize/event/${event?.id}`}
                target={appMode ? "_blank" : "_self"}
              />
            )}
          </Tabs>
        </Box>
      </Container>

      <ShareModal
        parentOpen={shareModalOpen}
        setParentOpen={setShareModalOpen}
        url={`${window.location.origin}/event/${event?.id}`}
        shareOptions={{
          twitter: `http://twitter.com/share?text=Checkout ${event?.name}${
            getEnvironmentVariable("COMPANY_NAME") === "Best Coast Pairings"
              ? " @BCPairings"
              : ""
          }&url=${window.location.origin}/event/${
            event?.id
          }&hashtags=${getEnvironmentVariable("COMPANY_NAME")
            .split(" ")
            .join("")},${event?.gameSystem?.name.split(" ").join("")}`,
          email: `mailto:?subject=Checkout ${
            event?.name
          } at ${getEnvironmentVariable("COMPANY_NAME")}&body=Checkout ${
            event?.name
          } at ${getEnvironmentVariable("COMPANY_NAME")} ${
            window.location.origin
          }/event/${event?.id}`
        }}
      />
    </Box>
  );
}

export async function hasAccess(event, authContext, tabIndex, notify) {
  var err;

  if (
    authContext?.authState?.identity?.isAdmin === true ||
    authContext?.authState?.identity?.adminGameSystems?.includes(
      event?.gameSystemId
    )
  ) {
    return true;
  }

  if (tabIndex === tabIndexes.overview) return true;

  if (event) {
    if (authContext?.authState?.identity?.userId === event.ownerId) {
      return true;
    } else {
      if (tabIndex === tabIndexes.placings && event.hidePlacings) {
        err = new Error(
          "Placings are currently being hidden until the organizer reveals them"
        );
        if (notify) postNotification("warning", err);
      }
      if (tabIndex === tabIndexes.pairings && !event.started) {
        err = new Error("The organizer has not started the event yet");
        if (notify) postNotification("warning", err);
      }
      if (
        tabIndex === tabIndexes["player-soft-scores"] &&
        !event.softScores?.filter((el) => el.playerScored)?.length
      ) {
        err = new Error("There are no player scored soft scores");
        if (notify) postNotification("warning", err);
      }
      if (
        NO_SUBSCRIPTIONS ||
        authContext?.authState?.identity?.subscriber ||
        authContext?.authState?.identity?.isAdmin
      ) {
        return true;
      } else {
        if (moment(event.eventEndDate).isBefore(moment().subtract(3, "d"))) {
          if (tabIndex === tabIndexes.pairings) {
            err = new Error(
              "Pairings for past events are hidden. You can unhide past events by becoming a Subscriber"
            );
            if (notify)
              postNotification(
                "warning",
                <>
                  Pairings for past events are hidden. You can unhide past
                  events by becoming a{" "}
                  <Link href="/subscription" underline="none" target="_blank">
                    Subscriber
                  </Link>
                </>
              );
          }
        }
      }
    }
  }
  if (err) {
    throw err;
  }
  return false;
}
